import type { WritableAtom } from 'jotai';
import { isEqual } from 'lodash-es';
import { isDebugMode } from '../../debug/debugUser';
import { getForesightStore } from '../../util/jotai-store';
import { decodeJwtPayload } from '../../util/jwt';
import { readAccessToken } from '../state/AuthState';
import { BootCacheStore, indexedDbBootCacheStore } from './bootCacheStore';

// The boot's reads change rarely, yet every reload fetched them again before anything rendered. Each is kept
// in IndexedDB: the next load renders from it at once and fetches behind it, and a response that differs
// replaces what was served. Off in debug mode, where the e2e suite intercepts these requests.

type Store = ReturnType<typeof getForesightStore>;

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

let backend: BootCacheStore | null | undefined;

function cacheStore(): BootCacheStore | null {
  if (backend === undefined) {
    backend = typeof indexedDB === 'undefined' ? null : indexedDbBootCacheStore();
  }
  return backend;
}

// The build, tenant and user the cache belongs to, from the token's claims. The token itself is never kept.
function cacheScope(store: Store): string | null {
  const { sub, tenantId } = decodeJwtPayload(readAccessToken(store)) ?? {};
  if (typeof sub !== 'string' || typeof tenantId !== 'string') {
    return null;
  }
  return [import.meta.env.VITE_DEPLOY_VERSION ?? 'local', tenantId, sub].join('|');
}

async function readEntry(cache: BootCacheStore, key: string, scope: string) {
  try {
    const entry = await cache.get(key);
    if (entry && entry.scope !== scope) {
      await clearBootCache();
      return undefined;
    }
    return entry && Date.now() - entry.savedAt <= MAX_AGE_MS ? entry : undefined;
  } catch {
    return undefined;
  }
}

function writeEntry(cache: BootCacheStore, key: string, scope: string, value: unknown) {
  void cache.set(key, { scope, savedAt: Date.now(), value }).catch(() => undefined);
}

// Each key is served from the cache once per page, for the boot. A later read of it (a reset, a reload
// counter) wants the server's answer.
const servedThisPage = new Set<string>();

/**
 * The boot read `fetcher` makes, served from the cache when this browser has it for the same build, tenant
 * and user, and fetched behind it. `onFresh` gets the response only when it differs from what was served.
 * Without a cached entry, or with the cache unavailable, it is the fetch alone.
 */
export function readThrough<T>(
  key: string,
  fetcher: () => Promise<T>,
  onFresh: (value: T) => void,
  store: Store = getForesightStore()
): Promise<T> {
  const cache = cacheStore();
  const scope = cacheScope(store);
  if (!cache || !scope || isDebugMode() || servedThisPage.has(key)) {
    return fetcher();
  }
  servedThisPage.add(key);

  return readEntry(cache, key, scope).then((cached) => {
    const fetched = fetcher().then((value) => {
      writeEntry(cache, key, scope, value);
      return value;
    });
    if (!cached) {
      return fetched;
    }
    const served = cached.value as T;
    // What was served may be changed in place by the app; the comparison is with what was read.
    const asRead = structuredClone(served);
    fetched
      .then((fresh) => {
        if (!isEqual(fresh, asRead)) {
          onFresh(fresh);
        }
      })
      .catch((error) => {
        // A refusal means this session may no longer read what was served: nothing is kept for the next
        // load, which then meets the boot's own handling. Otherwise the served value stands, and the
        // request's own error handling reports the failure.
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          void clearBootCache();
        }
      });
    return served;
  });
}

/**
 * {@link readThrough} for an atom's initial value. A response that differs becomes the atom's value, unless
 * something has written to the atom since (a rename, a new field), which the response could predate.
 */
export function readThroughAtom<T>(
  atom: WritableAtom<unknown, [T], void>,
  key: string,
  fetcher: () => Promise<T>,
  store: Store = getForesightStore()
): Promise<T> {
  const served: Promise<T> = readThrough(
    key,
    fetcher,
    (fresh) => {
      if (store.get(atom) === served) {
        store.set(atom, fresh);
      }
    },
    store
  );
  return served;
}

/** Empties the cache. Logout and the account switch wait for it, at most `withinMs`, before navigating. */
export async function clearBootCache(withinMs?: number): Promise<void> {
  const cache = cacheStore();
  if (!cache) {
    return;
  }
  const cleared = cache.clear().catch(() => undefined);
  await (withinMs === undefined
    ? cleared
    : Promise.race([cleared, new Promise((resolve) => setTimeout(resolve, withinMs))]));
}

export function __TEST_ONLY__useBootCacheStore(store: BootCacheStore | null) {
  backend = store;
  servedThisPage.clear();
}
